import React, { useState } from "react";
import {
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CSpinner,
} from "@coreui/react";
import { AutoPayDebitDetails } from "./AutoPayDebitDetails";
import { useAppSelector } from "../../../store/hooks";
import { AccountSummaryData } from "../../../AccountSummary.slice";
import { MODAL_ACTION_CANCELLED } from "./setAutopayPaymentMethod";
import { ModalAccountList } from "../../../components/ModalAccountList";
import { ServiceAccountLabel } from "../../../components/ServiceAccountLabel";
import {
  formatFollowingServiceAccountsLabel,
  formatOtherServiceAccountsCount,
  getServiceAccountScope,
} from "../../../utils/accountListDisplay";
import { ServiceAccountScopeAlert } from "../../../components/ServiceAccountScopeAlert";
import { PaymentMethodSourceLabel } from "../../PaymentMethods/components/PaymentMethodSourceLabel";
import { AutopayPaymentMethodOption } from "./autopayPaymentMethodOptions";

interface PropsInterface {
  action: "enable" | "disable";
  targetAccounts: AccountSummaryData[];
  paymentMethodId?: string;
  accountPaymentMethodsByAccount?: Record<string, AutopayPaymentMethodOption>;
  usePerAccountPaymentSources?: boolean;
  showUnaffectedAccountsHint?: boolean;
  resolve: any;
  reject: any;
}

const AutoPayConfirmModal: React.FC<PropsInterface> = (props) => {
  const {
    action,
    targetAccounts,
    paymentMethodId,
    accountPaymentMethodsByAccount = {},
    usePerAccountPaymentSources = false,
    showUnaffectedAccountsHint = false,
    resolve,
    reject,
  } = props;
  const [isSubmitting, setSubmitting] = useState(false);
  const { data, activeTenantAccountId } = useAppSelector(
    (state) => state.accountSummary
  );
  const isEnable = action === "enable";
  const hasMultipleServiceAccounts = (data ?? []).length > 1;
  const scope = getServiceAccountScope(targetAccounts, activeTenantAccountId);
  const targetAccount = targetAccounts[0];
  const otherAutopayEnabledAccounts =
    showUnaffectedAccountsHint && targetAccounts.length === 1
      ? (data ?? []).filter(
          (account) =>
            account.id !== targetAccount?.id && Boolean(account.autopay?.enabled)
        )
      : [];

  return (
    <CModal
      show
      onClose={() => {
        if (!isSubmitting) {
          reject(MODAL_ACTION_CANCELLED);
        }
      }}
    >
      <CModalHeader>
        <strong>Confirm AutoPay Preference</strong>
      </CModalHeader>
      <CModalBody>
        {isEnable && (
          <>
            {hasMultipleServiceAccounts && (
              <ServiceAccountScopeAlert scope={scope} />
            )}
            <p>
              By enabling AutoPay, I authorize King Energy to electronically
              debit{" "}
              {usePerAccountPaymentSources ? (
                <>
                  from the payment source selected for each service account
                </>
              ) : (
                <AutoPayDebitDetails
                  paymentMethodId={paymentMethodId}
                  account={targetAccount}
                />
              )}
              , and, if necessary, electronically credit my account to correct
              erroneous debits.
            </p>
            <p>
              I understand that subsequent debts of differing amounts will be
              automatically debited.
            </p>
            {targetAccounts.length > 0 ? (
              <>
                <p className="mb-2">
                  AutoPay will be enabled for{" "}
                  {formatFollowingServiceAccountsLabel(targetAccounts.length)}:
                </p>
                <ModalAccountList
                  accounts={targetAccounts}
                  renderLabel={(account) => (
                    <ServiceAccountLabel
                      account={account}
                      className="modal-account-list-item"
                      detailLine={
                        accountPaymentMethodsByAccount[account.id] ? (
                          <PaymentMethodSourceLabel
                            paymentMethod={
                              accountPaymentMethodsByAccount[account.id]
                            }
                          />
                        ) : undefined
                      }
                    />
                  )}
                />
              </>
            ) : (
              <p>Are you sure you want to enable AutoPay?</p>
            )}
          </>
        )}
        {!isEnable && (
          <>
            {hasMultipleServiceAccounts && (
              <ServiceAccountScopeAlert scope={scope} />
            )}
            {targetAccounts.length > 0 ? (
              <>
                <p className="mb-2">
                  AutoPay will be disabled for{" "}
                  {formatFollowingServiceAccountsLabel(targetAccounts.length)}:
                </p>
                <ModalAccountList
                  accounts={targetAccounts}
                  renderLabel={(account) => (
                    <ServiceAccountLabel
                      account={account}
                      className="modal-account-list-item"
                    />
                  )}
                />
              </>
            ) : (
              <p>Are you sure you want to disable AutoPay?</p>
            )}
            {otherAutopayEnabledAccounts.length > 0 && (
              <p className="text-muted mb-0 mt-3">
                {formatOtherServiceAccountsCount(
                  otherAutopayEnabledAccounts.length
                )}{" "}
                will remain enrolled in AutoPay. To disable AutoPay for another
                service account, select that account from the account switcher
                and disable AutoPay individually.
              </p>
            )}
          </>
        )}
      </CModalBody>
      <CModalFooter style={{ display: "flex" }}>
        <CButton
          color={isEnable ? "primary" : "danger"}
          style={{ flex: 2 }}
          disabled={isSubmitting}
          onClick={() => {
            setSubmitting(true);
            resolve();
          }}
        >
          {isSubmitting && (
            <CSpinner
              size="sm"
              style={{ marginRight: 8, verticalAlign: "middle" }}
            />
          )}
          {isEnable ? "Enable" : "Disable"} AutoPay
        </CButton>
        <CButton
          color="secondary"
          style={{ flex: 1 }}
          disabled={isSubmitting}
          onClick={() => reject(MODAL_ACTION_CANCELLED)}
        >
          Cancel
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default AutoPayConfirmModal;
